// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — FlowToolbar (M3: V·IANet Flow)
// Compact horizontal toolbar — Capa 1B redesign
// ═══════════════════════════════════════════════════════════════

import { useState, type CSSProperties } from 'react';
import { useI18n } from '../../../../../i18n';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

// ─── Props ───────────────────────────────────────────────────────────────────

export type AlignMode = 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

interface FlowToolbarProps {
  onSave: () => void;
  onVersionHistory?: () => void;
  onAuditTrail?: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onCollapseAll: () => void;
  onAutoLayout: () => void;
  isLayouting?: boolean;
  hasNodes?: boolean;
  onToggleGrid: () => void;
  onToggleMinimap: () => void;
  onExportPng: () => void;
  onExportPdf: () => void;
  onExportSvg?: () => void;
  showGrid: boolean;
  showMinimap: boolean;
  saving?: boolean;
  editing?: boolean;
  selectedCount?: number;
  onAlign?: (mode: AlignMode) => void;
  onDistribute?: (axis: DistributeAxis) => void;
  onMatchSize?: () => void;
  resizeEnabled?: boolean;
  onToggleResize?: () => void;
}

// ─── Icon SVGs ───────────────────────────────────────────────────────────────

function Icon({ d, size = 14 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

const ICONS = {
  save: 'M17 21v-8H7v8M7 3v5h8M5 3h11l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
  undo: 'M3 10h10a5 5 0 0 1 0 10H14M3 10l4-4M3 10l4 4',
  redo: 'M21 10H11a5 5 0 0 0 0 10h1M21 10l-4-4M21 10l-4 4',
  zoomIn: 'M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM21 21l-4.35-4.35M11 8v6M8 11h6',
  zoomOut: 'M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM21 21l-4.35-4.35M8 11h6',
  fitView: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
  collapse: 'M4 14h6v6M20 10h-6V4M4 14l6-6M20 10l-6 6',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  minimap: 'M1 6h2v12H1zM5 3h14v18H5zM21 6h2v12h-2z',
  image: 'M21 15l-5-5L5 21M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
  pdf: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  sparkle: 'M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z',
  chevronDown: 'M6 9l6 6 6-6',
  alignLeft: 'M4 4v16M8 8h10M8 16h6',
  alignCenterH: 'M12 4v16M7 8h10M8 16h8',
  alignRight: 'M20 4v16M6 8h10M10 16h6',
  alignTop: 'M4 4h16M8 8v10M16 8v6',
  alignCenterV: 'M4 12h16M8 7v10M16 8v8',
  alignBottom: 'M4 20h16M8 6v10M16 10v6',
  distributeH: 'M4 4v16M20 4v16M9 8h6M9 16h6',
  distributeV: 'M4 4h16M4 20h16M8 9v6M16 9v6',
  matchSize: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7z',
  resize: 'M21 15l-6 6M21 9l-12 12M21 3L3 21',
};

// ─── Separator ───────────────────────────────────────────────────────────────

function Separator() {
  return (
    <div
      style={{
        width: 1,
        height: 20,
        backgroundColor: T.color.border,
        margin: '0 3px',
        flexShrink: 0,
      }}
    />
  );
}

// ─── Icon Button ─────────────────────────────────────────────────────────────

function IconBtn({
  onClick,
  disabled,
  active,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);

  const style: CSSProperties = {
    width: 24,
    height: 24,
    padding: 0,
    borderRadius: 3,
    border: `1px solid ${active ? T.color.secondary : 'transparent'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.12s',
    opacity: disabled ? 0.35 : 1,
    color: active ? T.color.secondary : T.color.textSecondary,
    backgroundColor: active
      ? '#F0FBFF'
      : hovered && !disabled
        ? T.color.surfaceSubtle
        : 'transparent',
    fontFamily: T.font.family,
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
}

// ─── Text Button ─────────────────────────────────────────────────────────────

function TextBtn({
  onClick,
  title,
  children,
  variant = 'default',
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'accent';
}) {
  const [hovered, setHovered] = useState(false);

  const isPrimary = variant === 'primary';
  const isAccent = variant === 'accent';

  const style: CSSProperties = {
    padding: '2px 8px',
    borderRadius: 4,
    border: isPrimary ? 'none' : `1px solid ${isAccent ? T.color.secondary : T.color.border}`,
    cursor: 'pointer',
    fontSize: '8.5px',
    fontWeight: 600,
    fontFamily: T.font.family,
    transition: 'all 0.12s',
    color: isPrimary ? '#FFFFFF' : isAccent ? T.color.secondary : T.color.textSecondary,
    backgroundColor: isPrimary
      ? T.color.navy
      : hovered
        ? isAccent
          ? '#F0FBFF'
          : T.color.surfaceSubtle
        : 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    lineHeight: 1,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <button
      onClick={onClick}
      title={title}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FlowToolbar({
  onSave,
  onVersionHistory,
  onAuditTrail,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onZoomIn,
  onZoomOut,
  onFitView,
  onCollapseAll,
  onAutoLayout,
  isLayouting,
  hasNodes,
  onToggleGrid,
  onToggleMinimap,
  onExportPng,
  onExportPdf,
  onExportSvg,
  showGrid,
  showMinimap,
  saving,
  editing = false,
  selectedCount = 0,
  onAlign,
  onDistribute,
  onMatchSize,
  resizeEnabled,
  onToggleResize,
}: FlowToolbarProps) {
  const { t } = useI18n();
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '0 8px',
          height: 34,
          flexShrink: 0,
          backgroundColor: T.color.surface,
          borderBottom: `1px solid ${T.color.border}`,
        }}
      >
        {/* Group 1: Save, Undo, Redo (only in edit mode) */}
        {editing && (
          <IconBtn onClick={onSave} title={t.flow.guardarBorrador} disabled={saving}>
            <Icon d={ICONS.save} />
          </IconBtn>
        )}
        <IconBtn onClick={onUndo} disabled={!canUndo} title={t.flow.deshacer}>
          <Icon d={ICONS.undo} />
        </IconBtn>
        <IconBtn onClick={onRedo} disabled={!canRedo} title={t.flow.rehacer}>
          <Icon d={ICONS.redo} />
        </IconBtn>

        <Separator />

        {/* Group 2: Zoom */}
        <IconBtn onClick={onZoomIn} title={t.flow.acercar}>
          <Icon d={ICONS.zoomIn} />
        </IconBtn>
        <IconBtn onClick={onZoomOut} title={t.flow.alejar}>
          <Icon d={ICONS.zoomOut} />
        </IconBtn>
        <IconBtn onClick={() => onFitView()} title={t.flow.ajustarPantalla}>
          <Icon d={ICONS.fitView} />
        </IconBtn>

        <Separator />

        {/* Group 2b: Alignment */}
        {onAlign && (
          <>
            <IconBtn
              onClick={() => onAlign('left')}
              disabled={selectedCount < 2}
              title={t.flow.alinearIzquierda}
            >
              <Icon d={ICONS.alignLeft} />
            </IconBtn>
            <IconBtn
              onClick={() => onAlign('centerH')}
              disabled={selectedCount < 2}
              title={t.flow.alinearCentroH}
            >
              <Icon d={ICONS.alignCenterH} />
            </IconBtn>
            <IconBtn
              onClick={() => onAlign('right')}
              disabled={selectedCount < 2}
              title={t.flow.alinearDerecha}
            >
              <Icon d={ICONS.alignRight} />
            </IconBtn>
            <IconBtn
              onClick={() => onAlign('top')}
              disabled={selectedCount < 2}
              title={t.flow.alinearArriba}
            >
              <Icon d={ICONS.alignTop} />
            </IconBtn>
            <IconBtn
              onClick={() => onAlign('centerV')}
              disabled={selectedCount < 2}
              title={t.flow.alinearCentroV}
            >
              <Icon d={ICONS.alignCenterV} />
            </IconBtn>
            <IconBtn
              onClick={() => onAlign('bottom')}
              disabled={selectedCount < 2}
              title={t.flow.alinearAbajo}
            >
              <Icon d={ICONS.alignBottom} />
            </IconBtn>
          </>
        )}
        {onDistribute && (
          <>
            <IconBtn
              onClick={() => onDistribute('horizontal')}
              disabled={selectedCount < 3}
              title={t.flow.distribuirH}
            >
              <Icon d={ICONS.distributeH} />
            </IconBtn>
            <IconBtn
              onClick={() => onDistribute('vertical')}
              disabled={selectedCount < 3}
              title={t.flow.distribuirV}
            >
              <Icon d={ICONS.distributeV} />
            </IconBtn>
          </>
        )}
        {onMatchSize && (
          <IconBtn onClick={onMatchSize} disabled={selectedCount < 2} title={t.flow.igualarTamano}>
            <Icon d={ICONS.matchSize} />
          </IconBtn>
        )}

        <Separator />

        {/* Group 3: Auto-layout + Collapse */}
        <TextBtn onClick={onAutoLayout} title={t.flow.autoLayoutTitle}>
          {isLayouting ? t.flow.calculando : t.flow.autoLayout}
        </TextBtn>
        <IconBtn onClick={onCollapseAll} disabled={!hasNodes} title={t.flow.colapsarSubprocesos}>
          <Icon d={ICONS.collapse} />
        </IconBtn>

        {/* Group 4: Export dropdown */}
        <div style={{ position: 'relative' }}>
          <TextBtn onClick={() => setExportOpen(!exportOpen)} title={t.flow.exportar}>
            {t.flow.exportar}
            <Icon d={ICONS.chevronDown} size={10} />
          </TextBtn>
          {exportOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 2,
                backgroundColor: T.color.surface,
                border: `1px solid ${T.color.border}`,
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 50,
                minWidth: 100,
              }}
            >
              <button
                onClick={() => {
                  onExportPng();
                  setExportOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '4px 10px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  fontSize: '8.5px',
                  fontWeight: 500,
                  fontFamily: T.font.family,
                  color: T.color.textSecondary,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = T.color.surfaceSubtle;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                PNG
              </button>
              <button
                onClick={() => {
                  onExportPdf();
                  setExportOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '4px 10px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  fontSize: '8.5px',
                  fontWeight: 500,
                  fontFamily: T.font.family,
                  color: T.color.textSecondary,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = T.color.surfaceSubtle;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                PDF
              </button>
              {onExportSvg && (
                <button
                  onClick={() => {
                    onExportSvg();
                    setExportOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '4px 10px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    fontSize: '8.5px',
                    fontWeight: 500,
                    fontFamily: T.font.family,
                    color: T.color.textSecondary,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = T.color.surfaceSubtle;
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }}
                >
                  SVG
                </button>
              )}
            </div>
          )}
        </div>

        {/* Toggle buttons */}
        <IconBtn onClick={onToggleGrid} active={showGrid} title={t.flow.cuadricula}>
          <Icon d={ICONS.grid} />
        </IconBtn>
        <IconBtn onClick={onToggleMinimap} active={showMinimap} title={t.flow.minimapa}>
          <Icon d={ICONS.minimap} />
        </IconBtn>
        {onToggleResize && (
          <IconBtn
            onClick={onToggleResize}
            active={resizeEnabled}
            title={t.flow.toggleRedimensionar}
          >
            <Icon d={ICONS.resize} />
          </IconBtn>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Save indicator */}
        {saving && editing && (
          <span
            style={{
              fontSize: 8,
              color: T.color.textTertiary,
              marginRight: 4,
              fontFamily: T.font.family,
            }}
          >
            {t.flow.guardando}
          </span>
        )}

        <Separator />

        {/* Versiones + Audit buttons */}
        {onVersionHistory && (
          <TextBtn onClick={onVersionHistory} title={t.flow.historialVersiones}>
            {t.flow.versiones}
          </TextBtn>
        )}
        {onAuditTrail && (
          <TextBtn onClick={onAuditTrail} title={t.flow.auditTrail}>
            {t.flow.audit}
          </TextBtn>
        )}
      </div>

      {/* Export dropdown click-away */}
      {exportOpen && (
        <div
          onClick={() => setExportOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 40 }}
        />
      )}
    </>
  );
}
